/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Format Zero-Egress Subtitle Export Modal (Task 10.5.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Download,
  Copy,
  Check,
  FileText,
  HardDrive,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useSubtitleStudioStore,
  SubtitleExportFormat,
} from '../../stores/useSubtitleStudioStore';

export const SubtitleExportModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isExportModalOpen,
    exportFormat,
    audioFile,
    cues,
    setExportModalOpen,
    setExportFormat,
    exportDocument,
  } = useSubtitleStudioStore();

  const [copied, setCopied] = useState(false);
  const [savedToGallery, setSavedToGallery] = useState(false);

  if (!isExportModalOpen) return null;

  const content = exportDocument();
  const baseName = audioFile?.name.replace(/\.[^/.]+$/, '') || 'transcription_subtitles';

  const formatExtensions: Record<SubtitleExportFormat, string> = {
    srt: 'srt',
    vtt: 'vtt',
    txt: 'txt',
    csv: 'csv',
    json: 'json',
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleDownload = () => {
    const ext = formatExtensions[exportFormat];
    const mimeMap: Record<SubtitleExportFormat, string> = {
      srt: 'text/plain;charset=utf-8',
      vtt: 'text/vtt;charset=utf-8',
      txt: 'text/plain;charset=utf-8',
      csv: 'text/csv;charset=utf-8',
      json: 'application/json;charset=utf-8',
    };

    const blob = new Blob([content], { type: mimeMap[exportFormat] });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${baseName}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveToGallery = () => {
    setSavedToGallery(true);
    setTimeout(() => setSavedToGallery(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-primary/10 text-primary">
              <Download className="size-4" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-text">
                {t('subtitles.exportModalTitle')}
              </h3>
              <p className="text-xs text-text-muted">
                {cues.length} {t('subtitles.statsCues').toLowerCase()} • Zero-Egress Client Generation
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExportModalOpen(false)}
            className="p-1 rounded-lg hover:bg-surface-subtle text-text-muted hover:text-text transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Format Selectors */}
        <div className="px-5 pt-4 pb-2 border-b border-border bg-surface-subtle">
          <label className="text-xs font-semibold text-text block mb-2">
            {t('subtitles.exportFormatLabel')}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {(['srt', 'vtt', 'txt', 'csv', 'json'] as SubtitleExportFormat[]).map((fmt) => (
              <button
                key={fmt}
                type="button"
                onClick={() => setExportFormat(fmt)}
                className={`py-2 px-3 rounded-xl border text-center transition-all ${
                  exportFormat === fmt
                    ? 'bg-primary text-primary-foreground border-primary font-bold shadow-xs'
                    : 'bg-surface border-border hover:bg-surface-subtle text-text font-medium'
                }`}
              >
                <div className="text-xs uppercase font-mono">.{fmt}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Live Preview Container */}
        <div className="p-5 flex-1 overflow-hidden flex flex-col space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-text flex items-center gap-1.5">
              <FileText className="size-3.5 text-primary" />
              {t('subtitles.previewLabel')}
            </span>
            <span className="font-mono text-text-muted text-[11px]">
              {content.length} characters
            </span>
          </div>

          <div className="flex-1 overflow-y-auto rounded-xl border border-border bg-surface-subtle p-3.5 font-mono text-[11px] text-text whitespace-pre-wrap leading-relaxed select-all">
            {content}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-border bg-surface-subtle flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopy}
              className="px-3.5 py-2 text-xs font-medium rounded-xl border border-border bg-surface hover:bg-surface-subtle text-text transition-colors flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="size-3.5 text-emerald-500" />
                  <span className="text-emerald-500">{t('subtitles.copiedToast')}</span>
                </>
              ) : (
                <>
                  <Copy className="size-3.5" />
                  <span>{t('subtitles.copyBtn')}</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveToGallery}
              className="px-3.5 py-2 text-xs font-medium rounded-xl border border-border bg-surface hover:bg-surface-subtle text-text transition-colors flex items-center gap-1.5"
            >
              {savedToGallery ? (
                <>
                  <Sparkles className="size-3.5 text-amber-500" />
                  <span className="text-amber-500 font-semibold">{t('subtitles.savedToGalleryToast')}</span>
                </>
              ) : (
                <>
                  <HardDrive className="size-3.5" />
                  <span>{t('subtitles.saveToGalleryBtn')}</span>
                </>
              )}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExportModalOpen(false)}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-border bg-surface hover:bg-surface-subtle text-text transition-colors"
            >
              {t('subtitles.cancelBtn')}
            </button>

            <button
              type="button"
              onClick={handleDownload}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-sm flex items-center gap-2"
            >
              <Download className="size-4" />
              <span>{t('subtitles.downloadBtn')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
